import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, radius, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const SAKLAMA_GUN = 60
const saklamaSiniri = () =>
  new Date(Date.now() - SAKLAMA_GUN * 86400000).toISOString().slice(0, 10)

const style = {
  card: { ...s.card, padding: '24px' },
  input: { ...s.input, padding: '10px', fontSize: '14px' },
  label: { ...s.labelNormal, fontSize: '14px' },
  th: s.th,
  td: s.td,
  btnAdd: { ...s.btnSecondary, whiteSpace: 'nowrap' },
  btnDel: { ...ui.btnDangerQuiet, flex: '0 0 auto' },
}

const DEFAULT_CONFIG = {
  is_active: false,
  search_in_name: true,
  search_in_description: false,
  search_in_category: true,
  boost_instock: true,
  exact_match_first: true,
  hide_outofstock: false,
  popular_searches_mode: 'auto',
  manual_popular_searches: [],
  featured_category_codes: [],
  synonym_list: [],
  typewriter_texts: ['Kitap ara...', 'Yazar ara...', 'Kategori ara...'],
  before_banners: [],
  ui_labels: {},
}

const BANNER_SLOT = 6

function normalizeBanners(list) {
  const out = Array.isArray(list) ? list.slice(0, BANNER_SLOT).map(b => ({
    image_url: (b && b.image_url) || '',
    link_url: (b && b.link_url) || '',
    title: (b && b.title) || '',
  })) : []
  while (out.length < BANNER_SLOT) out.push({ image_url: '', link_url: '', title: '' })
  return out
}

export default function SmartSearch() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [categories, setCategories] = useState([])
  const [analytics, setAnalytics] = useState(null)
  const [tab, setTab] = useState('config')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [newPopular, setNewPopular] = useState('')
  const [newSynonymFrom, setNewSynonymFrom] = useState('')
  const [newSynonymTo, setNewSynonymTo] = useState('')
  const [dateFrom, setDateFrom] = useState(() => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10))
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10))

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  useEffect(() => {
    if (!selectedBrand) return
    fetchConfig()
    fetchCategories()
    if (tab === 'analytics') fetchAnalytics()
  }, [selectedBrand?.id, tab]) // eslint-disable-line

  function fetchConfig() {
    axios.get(`${API}/admin/brands/${selectedBrand.id}/search-config`, { headers })
      .then(res => {
        if (res.data && res.data.brand_id) {
          setConfig({ ...DEFAULT_CONFIG, ...res.data })
        }
      })
      .catch(() => {})
  }

  function fetchCategories() {
    axios.get(`${API}/admin/brands/${selectedBrand.id}/categories`, { headers })
      .then(res => setCategories(res.data.flat || []))
      .catch(() => {})
  }

  function fetchAnalytics(f = dateFrom, t = dateTo) {
    setAnalytics(null)
    axios.get(`${API}/admin/brands/${selectedBrand.id}/search-analytics?from=${f}&to=${t}`, { headers })
      .then(res => setAnalytics(res.data))
      .catch(() => {})
  }

  function applyDatePreset(days) {
    const f = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10)
    const t = new Date().toISOString().slice(0, 10)
    setDateFrom(f)
    setDateTo(t)
    fetchAnalytics(f, t)
  }

  async function handleSave() {
    setSaving(true)
    await axios.put(`${API}/admin/brands/${selectedBrand.id}/search-config`, config, { headers })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  function set(key, value) {
    setConfig(c => ({ ...c, [key]: value }))
  }

  function setLabel(key, value) {
    setConfig(c => ({ ...c, ui_labels: { ...(c.ui_labels || {}), [key]: value } }))
  }

  function setBanner(i, key, value) {
    const list = normalizeBanners(config.before_banners)
    list[i] = { ...list[i], [key]: value }
    set('before_banners', list)
  }

  function clearBanner(i) {
    const list = normalizeBanners(config.before_banners)
    list[i] = { image_url: '', link_url: '', title: '' }
    set('before_banners', list)
  }

  function addPopular() {
    if (!newPopular.trim()) return
    set('manual_popular_searches', [...(config.manual_popular_searches || []), newPopular.trim()])
    setNewPopular('')
  }

  function addTypewriterText() {
    if (!newPopular.trim()) return
    set('typewriter_texts', [...(config.typewriter_texts || []), newPopular.trim()])
    setNewPopular('')
  }
  function removeTypewriterText(i) {
    set('typewriter_texts', (config.typewriter_texts || []).filter((_, idx) => idx !== i))
  }

  function removePopular(i) {
    set('manual_popular_searches', config.manual_popular_searches.filter((_, idx) => idx !== i))
  }

  function toggleFeaturedCategory(code) {
    const current = config.featured_category_codes || []
    if (current.includes(code)) {
      set('featured_category_codes', current.filter(c => c !== code))
    } else {
      set('featured_category_codes', [...current, code])
    }
  }

  function addSynonym() {
    if (!newSynonymFrom.trim() || !newSynonymTo.trim()) return
    const list = config.synonym_list || []
    set('synonym_list', [...list, { from: newSynonymFrom.trim(), to: newSynonymTo.trim() }])
    setNewSynonymFrom('')
    setNewSynonymTo('')
  }

  function removeSynonym(i) {
    set('synonym_list', (config.synonym_list || []).filter((_, idx) => idx !== i))
  }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <p style={{ ...ui.emptyText, marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={s.pageTitle}>Smart Search — {selectedBrand.name}</h2>
          <p style={s.pageSubtitle}>AI destekli arama deneyimi</p>
        </div>
        <div className="sx-wrap" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={ui.status(config.is_active)}>
            <span style={ui.statusDot(config.is_active)} />
            {config.is_active ? 'Aktif' : 'Pasif'}
          </span>
          <button onClick={handleSave} disabled={saving} style={{ ...s.btnPrimary, padding: '10px 20px' }}>
            {saving ? 'Kaydediliyor...' : saved ? '✓ Kaydedildi' : 'Kaydet'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={ui.tabBar}>
        {[
          { key: 'config', label: 'Ayarlar' },
          { key: 'popular', label: 'Popüler Aramalar' },
          { key: 'categories', label: 'Öne Çıkan Kategoriler' },
          { key: 'synonyms', label: 'Eş Anlamlılar' },
          { key: 'typewriter', label: 'Arama Animasyonu' },
          { key: 'banners', label: 'Before Görselleri' },
          { key: 'missing_urls', label: 'Eksik URL\'ler' },
          { key: 'analytics', label: 'Analitik' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={ui.tab(tab === t.key)}>{t.label}</button>
        ))}
      </div>

      {/* Config Tab */}
      {tab === 'config' && (
        <div style={style.card}>
          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Durum</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
              <input type="checkbox" checked={config.is_active} onChange={e => set('is_active', e.target.checked)} />
              Smart Search aktif
            </label>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Arama Kapsamı</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { key: 'search_in_name', label: 'Ürün adında ara' },
                { key: 'search_in_description', label: 'Ürün açıklamasında ara' },
                { key: 'search_in_category', label: 'Kategori içinde ara' },
              ].map(item => (
                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={config[item.key] !== false} onChange={e => set(item.key, e.target.checked)} />
                  {item.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Sıralama Ayarları</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { key: 'boost_instock', label: 'Stokta olanları öne çıkar' },
                { key: 'exact_match_first', label: 'Tam eşleşmeleri önce göster' },
              ].map(item => (
                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={config[item.key] !== false} onChange={e => set(item.key, e.target.checked)} />
                  {item.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Stok Filtresi</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!config.hide_outofstock} onChange={e => set('hide_outofstock', e.target.checked)} />
                Stokta olmayan ürünleri arama sonuçlarında gizle
              </label>
              <div style={{ fontSize: '12px', color: colors.textSecondary, marginLeft: '24px' }}>
                Kapalıyken stokta olmayanlar "Stokta Yok" rozetiyle görünür.
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Arama Davranışı</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { key: 'typo_fix', label: 'Yazım hatasını düzelt' },
                { key: 'smart_match', label: 'Akıllı eşleşme' },
                { key: 'smart_enter', label: 'Enter\'a basınca akıllı yönlendir' },
                { key: 'search_in_keywords', label: 'Anahtar kelimelerde de ara' },
                { key: 'brand_priority', label: 'Marka adı önceliği' },
                { key: 'keep_before', label: 'Sonuç varken Before görsellerini koru' },
              ].map(item => (
                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!(config.ui_labels || {})[item.key]} onChange={e => setLabel(item.key, e.target.checked)} />
                  {item.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Görünüm</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { key: 'anchor_input', label: 'Arama kutusunu sabitle' },
                { key: 'history_hidden', label: 'Arama geçmişini gizle' },
                { key: 'popular_hidden', label: 'Popüler aramaları gizle' },
                { key: 'writers_hidden', label: 'Yazar bloğunu gizle' },
                { key: 'ordered_categories', label: 'Kategorileri sıralı göster' },
                { key: 'results_categories', label: 'Sonuçlarda kategori bloğu göster' },
                { key: 'results_categories_top', label: 'Kategori bloğunu sonuçların üstüne al' },
              ].map(item => (
                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!(config.ui_labels || {})[item.key]} onChange={e => setLabel(item.key, e.target.checked)} />
                  {item.label}
                </label>
              ))}
            </div>
            <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '8px' }}>
              Bu kutular kapalıyken ilgili davranış devre dışıdır.
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Metinler</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { key: 'banners_title', label: 'Before görselleri başlığı', ph: 'Size Özel Ürünler' },
                { key: 'categories_title', label: 'Popüler kategoriler başlığı', ph: 'Popüler Arama' },
                { key: 'results_categories_title', label: 'Sonuç kategorileri başlığı', ph: 'Kategoriler' },
                { key: 'empty_text', label: 'Sonuç bulunamadı metni', ph: 'Aradığınız ürün bulunamadı' },
                { key: 'empty_image', label: 'Sonuç bulunamadı görseli (URL)', ph: 'https://...' },
                { key: 'no_image_icon', label: 'Görseli olmayan ürün ikonu', ph: '🖼' },
              ].map(item => (
                <div key={item.key}>
                  <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '4px' }}>{item.label}</div>
                  <input
                    style={{ ...style.input, width: '100%' }}
                    value={(config.ui_labels || {})[item.key] || ''}
                    placeholder={item.ph}
                    onChange={e => setLabel(item.key, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '8px' }}>
              Boş bırakılan alanlarda widget kendi varsayılan metnini kullanır.
            </div>
          </div>
        </div>
      )}

      {/* Popular Tab */}
      {tab === 'popular' && (
        <div style={style.card}>
          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Popüler Arama Modu</label>
            <div className="sx-wrap" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {[
                { value: 'auto', label: 'Otomatik', desc: 'En çok aranan kelimeler' },
                { value: 'manual', label: 'Manuel', desc: 'Elle belirle' },
                { value: 'both', label: 'İkisi', desc: 'Otomatik + manuel' },
              ].map(o => (
                <div key={o.value} onClick={() => set('popular_searches_mode', o.value)} style={{
                  flex: '1 1 150px', minWidth: 0, padding: '12px', borderRadius: radius.md,
                  border: '1px solid ' + (config.popular_searches_mode === o.value ? colors.primary : colors.border),
                  cursor: 'pointer',
                  background: config.popular_searches_mode === o.value ? colors.bgSelected : colors.bgCard,
                }}>
                  <div style={{ fontWeight: 600, fontSize: '13px', color: colors.textPrimary }}>{o.label}</div>
                  <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '2px' }}>{o.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label style={style.label}>Manuel Popüler Aramalar</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input style={{ ...style.input, flex: 1, minWidth: 0 }} value={newPopular} onChange={e => setNewPopular(e.target.value)}
                placeholder="Arama terimi..." onKeyDown={e => e.key === 'Enter' && addPopular()} />
              <button onClick={addPopular} style={style.btnAdd}>Ekle</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {(config.manual_popular_searches || []).map((q, i) => (
                <span key={i} style={{ ...ui.chip, gap: '6px', color: colors.textPrimary }}>
                  {q}
                  <button onClick={() => removePopular(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: '14px', padding: '0' }}>×</button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {tab === 'categories' && (
        <div style={style.card}>
          <label style={style.label}>Öne Çıkan Kategoriler <span style={{ fontSize: '12px', color: colors.textMuted, fontWeight: 400 }}>(Before Search ekranında gösterilir)</span></label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', maxHeight: '400px', overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '12px' }}>
            {categories.filter(c => c.level === 1 || !c.level).map(cat => (
              <label key={cat.category_code} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', padding: '4px' }}>
                <input type="checkbox"
                  checked={(config.featured_category_codes || []).includes(cat.category_code)}
                  onChange={() => toggleFeaturedCategory(cat.category_code)} />
                {cat.category_name}
              </label>
            ))}
          </div>
          <p style={{ ...ui.numMuted, fontSize: '12px', marginTop: '8px' }}>
            Seçili: {(config.featured_category_codes || []).length} kategori
          </p>
        </div>
      )}

      {/* Synonyms Tab */}
      {tab === 'synonyms' && (
        <div style={style.card}>
          <p style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '16px' }}>
            Kullanıcı soldaki terimi aradığında sağdaki terimle de arama yapılır.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
            <input style={style.input} value={newSynonymFrom} onChange={e => setNewSynonymFrom(e.target.value)} placeholder="laptop" />
            <input style={style.input} value={newSynonymTo} onChange={e => setNewSynonymTo(e.target.value)} placeholder="notebook" />
            <button onClick={addSynonym} style={style.btnAdd}>Ekle</button>
          </div>
          <div className="sx-scroll-x">
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={style.th}>Kullanıcı Yazar</th>
                  <th style={style.th}>Sistem Arar</th>
                  <th style={style.th}></th>
                </tr>
              </thead>
              <tbody>
                {(config.synonym_list || []).map((s, i) => (
                  <tr key={i}>
                    <td style={style.td}>{s.from}</td>
                    <td style={style.td}>{s.to}</td>
                    <td style={{ ...style.td, textAlign: 'center' }}>
                      <button onClick={() => removeSynonym(i)} style={style.btnDel}>Sil</button>
                    </td>
                  </tr>
                ))}
                {!(config.synonym_list || []).length && (
                  <tr><td colSpan={3} style={{ ...style.td, ...ui.emptyText, padding: '16px', textAlign: 'center' }}>Henüz eş anlamlı eklenmedi</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'typewriter' && (
        <div style={style.card}>
          <p style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '16px' }}>
            Arama kutusunda sırayla yazılacak placeholder metinleri. Kullanıcı aramaya başlamadan önce görünür.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input style={{ ...style.input, flex: 1, minWidth: 0 }} value={newPopular} onChange={e => setNewPopular(e.target.value)}
              placeholder="örn: Tarih kitapları ara..." onKeyDown={e => e.key === 'Enter' && addTypewriterText()} />
            <button onClick={addTypewriterText} style={style.btnAdd}>Ekle</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(config.typewriter_texts || []).map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.sm }}>
                <span style={{ fontSize: '13px', color: colors.textPrimary, flex: 1 }}>{t}</span>
                <button onClick={() => removeTypewriterText(i)} style={style.btnDel}>Sil</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'banners' && (
        <div style={style.card}>
          <p style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '16px' }}>
            Arama kutusu açıldığında, kullanıcı henüz bir şey yazmadan görünen görsel alanı.
            Masaüstünde 3 sütun (2 sıra), mobilde 2 sütun olarak dizilir. Boş bırakılan satırlar gösterilmez.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {normalizeBanners(config.before_banners).map((b, i) => (
              <div key={i} className="sx-wrap" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.sm }}>
                <span style={{ ...ui.numMuted, fontSize: '12px', width: '16px', flex: '0 0 auto' }}>{i + 1}</span>
                <div style={{ width: '52px', height: '52px', flex: '0 0 auto', borderRadius: radius.sm, overflow: 'hidden', background: colors.bgHover, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {b.image_url
                    ? <img src={b.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.visibility = 'hidden' }} />
                    : <span style={{ fontSize: '11px', color: colors.textMuted }}>yok</span>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px', minWidth: 0 }}>
                  <input style={{ ...style.input, width: '100%', maxWidth: '100%' }} value={b.image_url}
                    onChange={e => setBanner(i, 'image_url', e.target.value)}
                    placeholder="Görsel URL (https://...)" />
                  <div className="sx-stack-sm" style={{ display: 'flex', gap: '6px' }}>
                    <input style={{ ...style.input, flex: 2, minWidth: 0 }} value={b.link_url}
                      onChange={e => setBanner(i, 'link_url', e.target.value)}
                      placeholder="Hedef URL (/kategori-linki veya https://...)" />
                    <input style={{ ...style.input, flex: 1, minWidth: 0 }} value={b.title}
                      onChange={e => setBanner(i, 'title', e.target.value)}
                      placeholder="Başlık (opsiyonel)" />
                  </div>
                </div>
                <button onClick={() => clearBanner(i)}
                  style={style.btnDel}>Temizle</button>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '12px', color: colors.textMuted, marginTop: '14px' }}>
            Hedef URL alanına site içi bir yol (örn. <code>/buz-makineleri</code>) ya da tam adres yazabilirsiniz.
            Görseller kare oranında kırpılarak gösterilir.
          </p>
        </div>
      )}

      {tab === 'missing_urls' && (
        <MissingUrlsTab brandId={selectedBrand?.id} headers={headers} />
      )}

      {/* Analytics Tab */}
      {tab === 'analytics' && (
        <div>
          <div style={{ ...style.card, marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '13px', color: colors.textSecondary }}>Başlangıç</label>
            <input type="date" value={dateFrom} min={saklamaSiniri()} max={dateTo} onChange={e => setDateFrom(e.target.value)}
              style={{ ...s.inputSm, width: 'auto', padding: '7px 10px', fontSize: '13px' }} />
            <label style={{ fontSize: '13px', color: colors.textSecondary }}>Bitiş</label>
            <input type="date" value={dateTo} min={dateFrom} onChange={e => setDateTo(e.target.value)}
              style={{ ...s.inputSm, width: 'auto', padding: '7px 10px', fontSize: '13px' }} />
            <button onClick={() => fetchAnalytics()} style={{ ...s.btnSecondary, padding: '8px 18px' }}>Uygula</button>
            <div className="sx-wrap" style={{ ...ui.segmented, marginLeft: 'auto', flexWrap: 'wrap', maxWidth: '100%' }}>
              {[7, 30, 60].map(d => (
                <button key={d} onClick={() => applyDatePreset(d)}
                  style={ui.segmentedBtn(false)}>Son {d} Gün</button>
              ))}
            </div>
          </div>
          {!analytics ? (
            <div style={{ ...style.card, ...ui.emptyText, textAlign: 'center' }}>Yükleniyor...</div>
          ) : (
            <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div style={{ ...style.card, minWidth: 0 }}>
                <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}>En Çok Arananlar</h3>
                <div className="sx-scroll-x">
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={style.th}>Arama</th>
                        <th style={{ ...style.th, textAlign: 'right' }}>Sayı</th>
                        <th style={{ ...style.th, textAlign: 'right' }}>Ort. Sonuç</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.topSearches?.map((s, i) => (
                        <tr key={i}>
                          <td style={style.td}>{s.query}</td>
                          <td style={{ ...style.td, ...ui.num, textAlign: 'right' }}>{s.count}</td>
                          <td style={{ ...style.td, ...ui.numMuted, textAlign: 'right' }}>{parseFloat(s.avg_results).toFixed(0)}</td>
                        </tr>
                      ))}
                      {!analytics.topSearches?.length && (
                        <tr><td colSpan={3} style={{ ...style.td, ...ui.emptyText, padding: '16px', textAlign: 'center' }}>Veri yok</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...style.card, minWidth: 0 }}>
                <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}>AI ile Tamamlananlar</h3>
                <div className="sx-scroll-x">
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={style.th}>Arama</th>
                        <th style={{ ...style.th, textAlign: 'right' }}>Sayı</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.aiCompleted?.map((s, i) => (
                        <tr key={i}>
                          <td style={style.td}>{s.query}</td>
                          <td style={{ ...style.td, ...ui.num, textAlign: 'right' }}>{s.count}</td>
                        </tr>
                      ))}
                      {!analytics.aiCompleted?.length && (
                        <tr><td colSpan={2} style={{ ...style.td, ...ui.emptyText, padding: '16px', textAlign: 'center' }}>Veri yok</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...style.card, minWidth: 0 }}>
                <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}>Sonuç Vermeyenler</h3>
                <div className="sx-scroll-x">
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={style.th}>Arama</th>
                        <th style={{ ...style.th, textAlign: 'right' }}>Sayı</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.noResults?.map((s, i) => (
                        <tr key={i}>
                          <td style={style.td}>{s.query}</td>
                          <td style={{ ...style.td, ...ui.num, textAlign: 'right' }}>{s.count}</td>
                        </tr>
                      ))}
                      {!analytics.noResults?.length && (
                        <tr><td colSpan={2} style={{ ...style.td, ...ui.emptyText, padding: '16px', textAlign: 'center' }}>Veri yok</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function MissingUrlsTab({ brandId, headers }) {
  const [cats, setCats] = useState([])
  const [urls, setUrls] = useState({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!brandId) return
    axios.get(`${API}/admin/brands/${brandId}/missing-seo-links`, { headers })
      .then(res => {
        setCats(res.data)
        const initial = {}
        res.data.forEach(c => { initial[c.category_code] = '' })
        setUrls(initial)
      })
      .catch(() => {})
  }, [brandId]) // eslint-disable-line

  async function handleSave() {
    setSaving(true)
    const updates = Object.entries(urls).filter(([, v]) => v.trim())
    await axios.post(`${API}/admin/brands/${brandId}/update-seo-links`, { updates }, { headers })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
    // Refresh
    const res = await axios.get(`${API}/admin/brands/${brandId}/missing-seo-links`, { headers })
    setCats(res.data)
  }

  return (
    <div style={style.card}>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ ...s.sectionTitle, margin: 0 }}>Eksik Kategori URL'leri</p>
          <p style={{ ...s.sectionSubtitle, margin: '4px 0 0' }}>{cats.length} kategoride URL tanımlı değil</p>
        </div>
        <button onClick={handleSave} disabled={saving} style={s.btnSecondary}>
          {saving ? 'Kaydediliyor...' : saved ? '✓ Kaydedildi' : 'Kaydet'}
        </button>
      </div>
      {cats.length === 0 ? (
        <p style={{ ...ui.emptyText, textAlign: 'center', padding: '32px' }}>Tüm kategorilerin URL'leri tanımlı</p>
      ) : (
        <div className="sx-scroll-x">
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...style.th, width: '40px' }}>Kod</th>
                <th style={style.th}>Kategori Adı</th>
                <th style={style.th}>URL (örn: cizgi-roman)</th>
              </tr>
            </thead>
            <tbody>
              {cats.map(c => (
                <tr key={c.category_code}>
                  <td style={{ ...style.td, ...ui.numMuted, fontSize: '11px', color: colors.textMuted }}>{c.category_code}</td>
                  <td style={{ ...style.td, fontWeight: 500 }}>{c.category_name}</td>
                  <td style={{ ...style.td, padding: '4px 8px' }}>
                    <input
                      style={{ ...style.input, fontSize: '12px', padding: '6px 10px', minWidth: '160px' }}
                      value={urls[c.category_code] || ''}
                      onChange={e => setUrls(u => ({ ...u, [c.category_code]: e.target.value }))}
                      placeholder="cizgi-roman"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}